/**
 * PNG decoder (1..16 bit, grey / RGB / palette, with or without alpha) that
 * preserves exact code values, plus colour metadata: cICP, sRGB, iCCP, gAMA,
 * cHRM and pHYs.
 */
import { decode, convertIndexedToRgb } from 'fast-png';
import { unzlibSync } from 'fflate';
import { PRIMARIES } from '../../core/color';
import type { EncodingHint, PrimariesId, TransferId } from '../../core/types';
import { defaultHint, makeFrame } from '../frame';
import { iccToHint, matchPrimaries, parseIcc } from '../icc';
import type { RawFrame } from '../../core/types';

export interface PngMeta {
  cicp?: { primaries: number; transfer: number };
  srgb?: boolean;
  icc?: Uint8Array;
  iccName?: string;
  gamma?: number;
  chrm?: { w: [number, number]; r: [number, number]; g: [number, number]; b: [number, number] };
  pixelsPerMetre?: number;
}

export function readPngChunks(b: Uint8Array): PngMeta {
  const meta: PngMeta = {};
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  let o = 8;
  while (o + 12 <= b.length) {
    const len = dv.getUint32(o);
    const type = String.fromCharCode(b[o + 4], b[o + 5], b[o + 6], b[o + 7]);
    const d = o + 8;
    if (d + len > b.length) break;
    switch (type) {
      case 'cICP':
        meta.cicp = { primaries: b[d], transfer: b[d + 1] };
        break;
      case 'sRGB':
        meta.srgb = true;
        break;
      case 'gAMA':
        meta.gamma = dv.getUint32(d) / 100000;
        break;
      case 'cHRM': {
        const v = (k: number) => dv.getUint32(d + 4 * k) / 100000;
        meta.chrm = { w: [v(0), v(1)], r: [v(2), v(3)], g: [v(4), v(5)], b: [v(6), v(7)] };
        break;
      }
      case 'iCCP': {
        let z = d;
        while (z < d + len && b[z] !== 0) z++;
        meta.iccName = new TextDecoder('latin1').decode(b.subarray(d, z));
        try {
          meta.icc = unzlibSync(b.subarray(z + 2, d + len));
        } catch {
          /* corrupt profile: ignore */
        }
        break;
      }
      case 'pHYs':
        if (b[d + 8] === 1 && dv.getUint32(d) === dv.getUint32(d + 4)) meta.pixelsPerMetre = dv.getUint32(d);
        break;
      case 'IDAT':
      case 'IEND':
        return meta;
    }
    o += 12 + len;
  }
  return meta;
}

/** ITU-T H.273 code points -> Aperto ids. */
export function cicpPrimaries(c: number): PrimariesId | null {
  return ({ 1: 'srgb', 9: 'bt2020', 12: 'p3' } as Record<number, PrimariesId>)[c] ?? null;
}

export function cicpTransfer(c: number): TransferId | null {
  // 1/6/14/15 are BT.709-family OETFs; decoded for display with BT.1886.
  return (
    (
      { 1: 'bt1886', 6: 'bt1886', 14: 'bt1886', 15: 'bt1886', 13: 'srgb', 16: 'pq', 18: 'hlg', 8: 'linear', 4: 'gamma' } as Record<
        number,
        TransferId
      >
    )[c] ?? null
  );
}

export function pngHint(m: PngMeta, isGray: boolean): EncodingHint {
  if (m.cicp) {
    const p = cicpPrimaries(m.cicp.primaries);
    const t = cicpTransfer(m.cicp.transfer);
    if (p && t)
      return {
        transfer: t,
        gamma: t === 'gamma' ? 2.2 : undefined,
        primaries: p,
        source: 'png-chunk',
        description: `cICP chunk (primaries ${m.cicp.primaries}, transfer ${m.cicp.transfer})`,
      };
  }
  if (m.srgb) return { transfer: 'srgb', primaries: 'srgb', source: 'png-chunk', description: 'sRGB chunk' };
  if (m.icc) {
    const info = parseIcc(m.icc);
    if (info) return iccToHint(info);
  }
  if (m.gamma) {
    const g = 1 / m.gamma;
    let primaries: PrimariesId = 'srgb';
    let chromaticities: EncodingHint['chromaticities'];
    if (m.chrm && !isGray) {
      primaries = matchPrimaries(m.chrm);
      if (primaries === 'custom') chromaticities = m.chrm;
    }
    const isSrgbish = Math.abs(m.gamma - 0.45455) < 0.0005 && primaries === 'srgb';
    return {
      transfer: Math.abs(g - 1) < 1e-3 ? 'linear' : isSrgbish ? 'srgb' : 'gamma',
      gamma: g,
      primaries,
      chromaticities,
      source: 'png-chunk',
      description: `gAMA chunk (file gamma ${m.gamma}${m.chrm ? ', cHRM primaries' : ''})${isSrgbish ? ' treated as sRGB' : ''}`,
    };
  }
  return defaultHint('No colour metadata: assuming sRGB (IEC 61966-2-1)');
}

function unpackLowBit(data: Uint8Array, w: number, h: number, depth: number, channels: number): Uint8Array {
  const out = new Uint8Array(w * h * channels);
  const rowBytes = Math.ceil((w * channels * depth) / 8);
  const per = 8 / depth;
  const mask = (1 << depth) - 1;
  for (let y = 0; y < h; y++)
    for (let i = 0; i < w * channels; i++) {
      const byte = data[y * rowBytes + Math.floor(i / per)];
      const shift = 8 - depth * ((i % per) + 1);
      out[y * w * channels + i] = (byte >> shift) & mask;
    }
  return out;
}

export function decodePng(bytes: Uint8Array, name: string): RawFrame {
  const png = decode(bytes);
  const meta = readPngChunks(bytes);
  let data = png.data as Uint8Array | Uint16Array;
  let channels = png.channels;
  let depth: number = png.depth;
  const warnings: string[] = [];
  if (png.palette) {
    const idx = depth < 8 ? unpackLowBit(data as Uint8Array, png.width, png.height, depth, 1) : data;
    data = convertIndexedToRgb({ ...png, data: idx, depth: 8 } as Parameters<typeof convertIndexedToRgb>[0]) as Uint8Array;
    channels = data.length / (png.width * png.height);
    depth = 8;
  } else if (depth < 8) {
    data = unpackLowBit(data as Uint8Array, png.width, png.height, depth, channels);
  }
  const maxCode = png.palette ? 255 : (1 << depth) - 1;
  const encoding = pngHint(meta, channels <= 2);
  const info: Record<string, string> = { 'Bit depth': `${png.depth}`, Channels: `${png.channels}${png.palette ? ' (palette)' : ''}` };
  if (meta.iccName) info['ICC profile'] = meta.iccName;
  if (encoding.primaries !== 'custom' && encoding.primaries) info['Primaries'] = PRIMARIES[encoding.primaries].label;
  return makeFrame({
    width: png.width,
    height: png.height,
    data,
    channels,
    maxCode,
    bitDepth: depth,
    format: 'png',
    name,
    encoding,
    info,
    warnings,
    pixelsPerMm: meta.pixelsPerMetre ? meta.pixelsPerMetre / 1000 : undefined,
  });
}
